import { DataTableColumnHeader } from '@/components/admin/members/ColHeader'
import { UserEditor } from '@/components/sudo/user/Editor'
import { OrganizationsEditor } from '@/components/sudo/user/OrganizationsEditor'
import { PasswordEditor } from '@/components/sudo/user/PasswordEditor'
import { RootState } from '@/store'
import { sudoStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { SudoContextUser } from '@/store/sudo'
import { Avatar, AvatarFallback } from '@repo/ui/avatar'
import { DataProvider, useAsyncData } from '@repo/ui/dataProvider'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { Cfl, cn } from '@repo/ui/utils'
import { ColumnDef } from '@tanstack/react-table'
import {
  Building2,
  EllipsisVertical,
  ShieldAlert,
  UserPen,
  UserPlus,
  UserX,
} from 'lucide-react'
import { useCallback, useState } from 'react'

export const usersColumns: ColumnDef<SudoContextUser>[] = [
  {
    id: 'actions',
    cell: ({ row }) => {
      const dispatch = useStoreDispatch()
      const user = row.original
      const [openUserEditor, setOpenUserEditor] = useState(false)
      const [openPasswordEditor, setOpenPasswordEditor] = useState(false)
      const [openOrganizationsEditor, setOpenOrganizationsEditor] =
        useState(false)

      const selectUser = useCallback(() => {
        void dispatch(sudoStoreActions.user.select({ userId: user.id }))
        setOpenOrganizationsEditor(true)
      }, [])

      const {
        data: [me],
      } = useAsyncData([(state) => state.user.me])

      return (
        <>
          {openUserEditor && (
            <DataProvider
              selectors={[(state: RootState) => state.sudo.organizations]}
              render={(organizations) => (
                <UserEditor
                  modalHandler={{
                    open: openUserEditor,
                    setOpen: setOpenUserEditor,
                  }}
                  user={user}
                  organizations={organizations}
                />
              )}
            />
          )}
          {openPasswordEditor && (
            <PasswordEditor
              modalHandler={{
                open: openPasswordEditor,
                setOpen: setOpenPasswordEditor,
              }}
              userId={user.id}
            />
          )}
          {openOrganizationsEditor && (
            <DataProvider
              selectors={[
                (state: RootState) => state.sudo.organizations,
                (state: RootState) => state.sudo.caseWorkers,
              ]}
              render={(organizations, caseWorkers) => (
                <OrganizationsEditor
                  caseWorkers={caseWorkers}
                  modalHandler={{
                    open: openOrganizationsEditor,
                    setOpen: setOpenOrganizationsEditor,
                  }}
                  organizations={organizations}
                  user={user}
                />
              )}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="hover:text-primary-600 text-primary-300 data-[state=open]:text-primary-600 cursor-pointer px-2">
                <EllipsisVertical className="size-5" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="bottom" className="w-fit">
              <DropdownMenuLabel className="select-none p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarFallback className="text-primary rounded-lg">
                      {user.firstName.charAt(0)}
                      {user.lastName?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user.firstName} {user.lastName}
                    </span>
                    <span className="truncate text-xs">{user.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuItem onClick={() => setOpenUserEditor(true)}>
                  <UserPen className="size-4" />
                  <span>Edit user</span>
                </DropdownMenuItem>

                {!user.disabled && (
                  <DropdownMenuItem onClick={selectUser}>
                    <Building2 className="size-4" />
                    <span>Edit organizations</span>
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem onClick={() => setOpenPasswordEditor(true)}>
                  <ShieldAlert className="size-4" />
                  <span>Edit password</span>
                </DropdownMenuItem>

                {user.id !== me?.id &&
                  (user.disabled ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() =>
                          void dispatch(
                            sudoStoreActions.user.enable({
                              userId: user.id,
                            }),
                          )
                        }
                      >
                        <UserPlus className="size-4" />
                        <span>Enable</span>
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-red-500 focus:bg-red-100 focus:text-red-500"
                        onClick={() =>
                          void dispatch(
                            sudoStoreActions.user.disable({
                              userId: user.id,
                            }),
                          )
                        }
                      >
                        <UserX className="size-4" />
                        <span>Disable</span>
                      </DropdownMenuItem>
                    </>
                  ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )
    },
  },
  {
    accessorKey: 'lastName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{ sortable: true, searchable: true }}
        />
      )
    },
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{ sortable: true, searchable: true }}
        />
      )
    },
  },
  {
    accessorKey: 'email',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          column={column}
          options={{ sortable: true, searchable: true }}
        />
      )
    },
  },
  {
    accessorKey: 'disabled',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          title="Status"
          column={column}
          options={{ sortable: true }}
        />
      )
    },
    cell: ({ row, column }) => {
      const disabled = row.getValue<boolean>(column.id)
      return (
        <div className={cn(disabled && 'opacity-50')}>
          {Cfl(disabled ? 'disabled' : 'enabled')}
        </div>
      )
    },
  },
  {
    accessorKey: 'superAdmin',
    header: ({ column }) => {
      return (
        <DataTableColumnHeader
          title="Role"
          column={column}
          options={{ sortable: true }}
        />
      )
    },
    cell: ({ row, column }) => {
      const superAdmin = row.getValue<boolean>(column.id)
      return (
        <div className={cn(superAdmin && 'text-highlight')}>
          {Cfl(superAdmin ? 'Super Admin' : 'User')}
        </div>
      )
    },
  },
]
